import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Skeleton, StatusBadge,
} from '../../components/ui'
import { RouteIcon } from '../../components/icons'

/* Dispatches waiting to be closed off.
 *
 * Whoever moves a load records it; someone with oversight signs it off - a
 * super admin, a platform admin, or a project manager. A load touching
 * private works is closable by a super admin ALONE, and never appears in
 * this list for anyone else (the server filters it, so the count in the
 * menu badge cannot hint at it either). */

interface OpenLoad {
  id?: string
  ticket_no?: string
  plate?: string
  mineral_type?: string
  tonnage?: number | string
  status?: string
  destination?: string
  dispatcher_name?: string
  load_time?: string
  created_at?: string
}

export default function DispatchClosure() {
  const qc = useQueryClient()
  const [closing, setClosing] = useState<string | null>(null)
  const [notes, setNotes] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'dispatch', 'pending-closure'],
    queryFn: () => api.get<{ ok: boolean; items: OpenLoad[]; count: number }>(
      '/ops/dispatch/pending-closure'),
  })

  const close = useMutation({
    mutationFn: (id: string) =>
      api.post(`/ops/dispatch/loads/${id}/close`, { delivery_notes: notes.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'dispatch', 'pending-closure'] })
      void qc.invalidateQueries({ queryKey: ['queue-counts'] })
      setClosing(null)
      setNotes('')
    },
  })

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dispatch closure</h1>
          <p className="page-head__sub">
            Loads recorded in the field and waiting to be signed off.
          </p>
        </div>
      </div>

      {isError && <Alert tone="danger">{(error as Error).message}</Alert>}
      {close.isError && (
        <Alert tone="danger">
          {close.error instanceof ApiError ? close.error.message : 'Could not close that dispatch.'}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Awaiting closure"
          sub={items.length ? `${items.length} waiting` : undefined}
        />
        <CardBody flush>
          {isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={22} /><Skeleton h={22} w="76%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<RouteIcon size={22} />}
              title="Nothing waiting"
              body="Every dispatch has been closed off. New ones appear here as they are recorded."
            />
          ) : (
            <div>
              {items.map((l) => (
                <div key={l.id} className="lrow" style={{ display: 'block' }}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
                    <div className="lrow__main">
                      <div className="lrow__title">
                        <Link to={`/dispatch/${l.id}`}>{l.ticket_no || l.plate || l.id}</Link>
                      </div>
                      <div className="lrow__sub">
                        {[
                          l.plate,
                          l.mineral_type ? titleCase(l.mineral_type) : '',
                          l.tonnage ? `${formatNumber(l.tonnage, 1)} t` : '',
                          l.destination,
                          l.dispatcher_name ? `by ${l.dispatcher_name}` : '',
                          formatDate(l.load_time || l.created_at),
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="lrow__end" style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                      <StatusBadge status={l.status || 'dispatched'} />
                      {closing === l.id ? null : (
                        <Button size="sm" onClick={() => { setClosing(l.id ?? null); setNotes('') }}>
                          Close off
                        </Button>
                      )}
                    </div>
                  </div>

                  {closing === l.id && (
                    <div className="stack" style={{ gap: 'var(--sp-3)', marginTop: 'var(--sp-3)' }}>
                      <Input
                        label="Closing note (optional)"
                        placeholder="Anything worth recording about this delivery"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                      <div className="row" style={{ gap: 'var(--sp-2)' }}>
                        <Button
                          size="sm"
                          loading={close.isPending}
                          onClick={() => l.id && close.mutate(l.id)}
                        >
                          Confirm closure
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setClosing(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
